/**
 * Firestore-backed chat storage.
 * Keeps a small in-memory cache per presentation+slide and exposes live updates.
 */

import type { ChatMessage, ChatStorage } from '../core/types';
import { ensureFirestoreDb, getMessagesCollection, type FirestoreServiceOptions } from './firestore-service';

interface CreateFirestoreChatStorageOptions extends FirestoreServiceOptions {}

function cacheKey(presId: string, slideIndex: number): string {
  return presId + '|' + slideIndex;
}

function readCreatedAt(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (raw && typeof raw === 'object' && 'toMillis' in (raw as { toMillis?: unknown }) && typeof (raw as { toMillis?: () => number }).toMillis === 'function') {
    return (raw as { toMillis: () => number }).toMillis();
  }
  return Date.now();
}

function mapSnapshotDoc(doc: { id: string; data(): Record<string, unknown> }): ChatMessage {
  const d = doc.data() || {};
  return {
    id: doc.id,
    text: String(d.text ?? ''),
    userId: d.userId ? String(d.userId) : (d.uid ? String(d.uid) : undefined),
    uid: d.uid ? String(d.uid) : (d.userId ? String(d.userId) : undefined),
    displayName: d.displayName ? String(d.displayName) : undefined,
    authorEmail: d.authorEmail ? String(d.authorEmail) : undefined,
    authorRole: d.authorRole === 'admin' ? 'admin' : (d.authorRole === 'presenter' ? 'presenter' : undefined),
    authorColor: d.authorColor ? String(d.authorColor) : undefined,
    replyToId: d.replyToId ? String(d.replyToId) : undefined,
    replyToAuthor: d.replyToAuthor ? String(d.replyToAuthor) : undefined,
    replyToText: d.replyToText ? String(d.replyToText) : undefined,
    createdAt: readCreatedAt(d.createdAt)
  };
}

export function createFirestoreChatStorage(opts: CreateFirestoreChatStorageOptions): ChatStorage {
  const cache = new Map<string, ChatMessage[]>();

  return {
    getMessages(presId: string, slideIndex: number): ChatMessage[] {
      return cache.get(cacheKey(presId, slideIndex)) ?? [];
    },

    async addMessage(presId: string, slideIndex: number, msg: Partial<ChatMessage>): Promise<string | undefined> {
      const db = await ensureFirestoreDb(opts);
      const col = getMessagesCollection(db, presId, slideIndex);
      const payload: Record<string, unknown> = {
        text: String(msg.text ?? ''),
        userId: msg.userId ?? msg.uid ?? null,
        displayName: msg.displayName ?? null,
        authorEmail: msg.authorEmail ?? null,
        authorRole: msg.authorRole ?? null,
        authorColor: msg.authorColor ?? null,
        replyToId: msg.replyToId ?? null,
        replyToAuthor: msg.replyToAuthor ?? null,
        replyToText: msg.replyToText ?? null,
        createdAt: Date.now()
      };
      const ref = (await col.add(payload)) as { id?: string };
      return ref && typeof ref.id === 'string' ? ref.id : undefined;
    },

    async deleteMessage(presId: string, slideIndex: number, msgId: string): Promise<void> {
      const db = await ensureFirestoreDb(opts);
      const col = getMessagesCollection(db, presId, slideIndex) as unknown as {
        doc(id: string): { update(data: Record<string, unknown>): Promise<void>; delete(): Promise<void> };
      };
      const docRef = col.doc(String(msgId));
      try {
        await docRef.update({ deleted: true });
      } catch {
        await docRef.delete();
      }
    },

    subscribeMessages(
      presId: string,
      slideIndex: number,
      cb: (messages: ChatMessage[]) => void,
      onError?: (err: Error) => void
    ): () => void {
      let closed = false;
      let firestoreUnsub: (() => void) | null = null;

      ensureFirestoreDb(opts)
        .then((db) => {
          if (closed) return;
          const col = getMessagesCollection(db, presId, slideIndex);
          firestoreUnsub = col.onSnapshot(
            (snap) => {
              const list = (snap.docs || [])
                .filter((doc) => {
                  const d = doc.data() || {};
                  return d.deleted !== true;
                })
                .map(mapSnapshotDoc)
                .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
              cache.set(cacheKey(presId, slideIndex), list);
              cb(list.slice());
            },
            (err) => {
              const e = err instanceof Error ? err : new Error(String(err));
              onError?.(e);
            }
          );
        })
        .catch((err) => {
          const e = err instanceof Error ? err : new Error(String(err));
          onError?.(e);
        });

      return () => {
        closed = true;
        if (firestoreUnsub) firestoreUnsub();
      };
    }
  };
}
